// App-wide voice-note playback — the web's VoicePlayback.swift (Android: VoiceNotePlayback).
//
// A singleton because the rule it enforces is global: ONE note plays at a time, starting a second
// stops the first (conversation, thread pane and Files tab alike), and the chosen speed sticks
// across notes — someone who listens at 1.5× listens to everything at 1.5×. Finished = back to the
// start, paused, ready to replay.
//
// Like the apps, a note is DOWNLOADED first and then played from the local copy (the apps' file
// cache; here a blob URL, a few kept): seeking and the duration are then exact in every browser,
// whatever the server says about ranges. A note this tab just sent plays from the reader's own
// bytes (localFiles) without any download.
import { useSyncExternalStore } from 'react';
import { AttachmentKind } from '../../../core/attachmentKind';
import { VoiceNoteSeek } from '../../../core/voiceNoteControls';
import { mediaUrls } from '../../media';
import { localUrl } from '../attachments/localFiles';

export interface NoteState {
  fileId: string;
  playing: boolean;
  loading: boolean;
  /** Seconds. */
  position: number;
  duration: number;
  rate: number;
}

export interface PlaybackSnapshot {
  note: NoteState | null;
  rate: number;
  /** The last note that could not be loaded or played (its card says so). */
  failedId: string | null;
}

const CACHE_LIMIT = 12;

class VoicePlaybackController {
  private snap: PlaybackSnapshot = { note: null, rate: 1, failedId: null };
  private listeners = new Set<() => void>();
  private audio: HTMLAudioElement | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;
  /** fileId → blob URL, oldest first. */
  private cache = new Map<string, string>();
  private loads = 0;

  get snapshot(): PlaybackSnapshot {
    return this.snap;
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => void this.listeners.delete(l);
  };

  private emit(patch: Partial<PlaybackSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const l of this.listeners) l();
  }

  private patchNote(fileId: string, patch: Partial<NoteState>): void {
    const note = this.snap.note;
    if (note?.fileId !== fileId) return;
    this.emit({ note: { ...note, ...patch } });
  }

  /** One element for the whole app (a browser that unlocked it once for sound keeps it unlocked). */
  private element(): HTMLAudioElement {
    if (this.audio) return this.audio;
    const audio = new Audio();
    audio.preload = 'auto';
    audio.preservesPitch = true;
    audio.addEventListener('loadedmetadata', () => {
      const id = this.snap.note?.fileId;
      if (id) this.patchNote(id, { duration: Number.isFinite(audio.duration) ? audio.duration : 0 });
    });
    audio.addEventListener('ended', () => {
      // Back to the start, paused — ready to replay, like every voice-note player.
      this.stopTicker();
      audio.currentTime = 0;
      const id = this.snap.note?.fileId;
      if (id) this.patchNote(id, { playing: false, position: 0 });
    });
    audio.addEventListener('pause', () => {
      // Paused from outside (the OS media keys, another tab taking audio focus on a phone).
      const note = this.snap.note;
      if (note?.playing && !audio.ended) {
        this.stopTicker();
        this.patchNote(note.fileId, { playing: false, position: audio.currentTime });
      }
    });
    audio.addEventListener('error', () => {
      const id = this.snap.note?.fileId;
      if (id && audio.getAttribute('src')) this.fail(id);
    });
    this.audio = audio;
    return audio;
  }

  /** Play/pause this note; a different note stops whatever was playing. */
  toggle(fileId: string, mimeType: string): void {
    const current = this.snap.note;
    const audio = this.element();
    if (current && current.fileId === fileId && !current.loading) {
      if (current.playing) {
        audio.pause();
        this.stopTicker();
        this.patchNote(fileId, { playing: false, position: audio.currentTime });
      } else {
        audio.playbackRate = this.snap.rate;
        this.play(fileId);
      }
      return;
    }
    this.stop();
    this.emit({ note: { fileId, playing: false, loading: true, position: 0, duration: 0, rate: this.snap.rate }, failedId: this.snap.failedId === fileId ? null : this.snap.failedId });
    const ready = this.cache.get(fileId) ?? localUrl(fileId);
    if (ready) {
      // Still inside the click: the browser's autoplay rule is satisfied.
      this.startWith(fileId, ready);
      return;
    }
    const ticket = ++this.loads;
    fetch(mediaUrls.file(fileId), { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((blob) => {
        // A relayed file can arrive as octet-stream; the player needs the audio type (Safari).
        const typed = blob.type.startsWith('audio/') || blob.type.startsWith('video/') ? blob : new Blob([blob], { type: mimeType || 'audio/mp4' });
        const url = URL.createObjectURL(typed);
        this.remember(fileId, url);
        // The reader may have tapped another note while this one downloaded.
        if (ticket !== this.loads || this.snap.note?.fileId !== fileId) return;
        this.startWith(fileId, url);
      })
      .catch((e) => {
        console.warn('[satori-voice] could not load the note', fileId, e);
        if (ticket === this.loads) this.fail(fileId);
      });
  }

  private startWith(fileId: string, url: string): void {
    const audio = this.element();
    audio.src = url;
    audio.defaultPlaybackRate = this.snap.rate;
    audio.playbackRate = this.snap.rate;
    this.play(fileId);
  }

  private play(fileId: string): void {
    const audio = this.element();
    audio.play().then(
      () => {
        if (this.snap.note?.fileId !== fileId) return;
        this.patchNote(fileId, {
          playing: true,
          loading: false,
          rate: this.snap.rate,
          duration: Number.isFinite(audio.duration) ? audio.duration : this.snap.note.duration,
        });
        this.startTicker();
      },
      (e: DOMException) => {
        if (this.snap.note?.fileId !== fileId) return;
        // A browser that wants a fresh tap (the download outlived the click): loaded and paused,
        // so the next tap plays at once. Anything else is a note this browser cannot play.
        if (e?.name === 'NotAllowedError') this.patchNote(fileId, { loading: false, playing: false });
        else if (e?.name !== 'AbortError') this.fail(fileId);
      },
    );
  }

  private fail(fileId: string): void {
    this.stopTicker();
    if (this.snap.note?.fileId === fileId) this.emit({ note: null, failedId: fileId });
    else this.emit({ failedId: fileId });
  }

  private remember(fileId: string, url: string): void {
    this.cache.delete(fileId);
    this.cache.set(fileId, url);
    while (this.cache.size > CACHE_LIMIT) {
      const [oldest, oldUrl] = this.cache.entries().next().value as [string, string];
      if (oldest === this.snap.note?.fileId) break;
      this.cache.delete(oldest);
      URL.revokeObjectURL(oldUrl);
    }
  }

  /** The seek bar — anywhere in the note, forward or back. */
  seek(fraction: number): void {
    const note = this.snap.note;
    if (!note || note.loading || !(note.duration > 0) || !this.audio) return;
    const target = Math.min(Math.max(fraction, 0), 1) * note.duration;
    this.audio.currentTime = target;
    this.patchNote(note.fileId, { position: target });
  }

  /** The back/forward buttons: a known five seconds. */
  skip(delta: number): void {
    const note = this.snap.note;
    if (!note || note.loading || !this.audio) return;
    const target = VoiceNoteSeek.target(note.position, delta, note.duration);
    if (target === note.position) return;
    this.audio.currentTime = target;
    this.patchNote(note.fileId, { position: target });
  }

  cycleRate(): void {
    const rate = AttachmentKind.nextRate(this.snap.rate);
    this.emit({ rate });
    const note = this.snap.note;
    if (this.audio) {
      this.audio.defaultPlaybackRate = rate;
      this.audio.playbackRate = rate;
    }
    if (note) this.patchNote(note.fileId, { rate });
  }

  stop(): void {
    this.stopTicker();
    this.loads += 1;
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }
    if (this.snap.note) this.emit({ note: null });
  }

  private startTicker(): void {
    this.stopTicker();
    this.ticker = setInterval(() => {
      const note = this.snap.note;
      if (!note?.playing || !this.audio) return;
      this.patchNote(note.fileId, { position: this.audio.currentTime });
    }, 200);
  }

  private stopTicker(): void {
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
  }
}

export const voicePlayback = new VoicePlaybackController();

export function useVoicePlayback(): PlaybackSnapshot {
  return useSyncExternalStore(voicePlayback.subscribe, () => voicePlayback.snapshot);
}
